import * as signalR from '@microsoft/signalr';

// Generic upload progress interface
export interface UploadProgress {
  operationId: string;
  progressPercentage: number;
  message: string;
  processedRecords: number;
  totalRecords: number;
  timestamp: string;
  status: 'Starting' | 'InProgress' | 'Completed' | 'Failed' | 'Cancelled';
  uploadType: string;
}



export type ProgressCallback = (progress: UploadProgress) => void;
export type CompletionCallback = (progress: UploadProgress) => void;
export type ErrorCallback = (progress: UploadProgress) => void;

class SignalRService {
  private connections: Map<string, signalR.HubConnection> = new Map();

  private getServiceBaseUrl(serviceName: string): string {
    const envKey = `REACT_APP_${serviceName}_API_BASE_URL`;
    const baseUrl = (process.env as any)[envKey];
    
    if (!baseUrl) {
      throw new Error(`Environment variable ${envKey} not found for service ${serviceName}`);
    }
    
    // Remove trailing slash if present
    return baseUrl.replace(/\/$/, '');
  }

  private getHubName(serviceName: string): string {
    return `${serviceName.toLowerCase()}_hub`;
  }

  private getConnectionKey(serviceName: string): string {
    return serviceName.toLowerCase();
  }

  // The factory, not a token: SignalR calls it on every negotiate, including each automatic
  // reconnect. A token captured at page load expires after about an hour, and every reconnect then
  // re-presented it and got 401 until the retries ran out - a two-hour catalog load lost its
  // progress channel at 59 minutes with nothing on the page (spec 131 verification, 2026-09-15).
  async connect(serviceName: string, accessTokenFactory: () => Promise<string | undefined>): Promise<void> {
    const connectionKey = this.getConnectionKey(serviceName);
    const existingConnection = this.connections.get(connectionKey);
    
    if (existingConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    const baseUrl = this.getServiceBaseUrl(serviceName);
    const hubName = this.getHubName(serviceName);
    const hubUrl = `${baseUrl}/${hubName}`;
    
    console.log(`Attempting SignalR connection to ${serviceName} service:`, hubUrl);
    
    const connectionBuilder = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        accessTokenFactory: async () => (await accessTokenFactory()) ?? '',
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information);

    const connection = connectionBuilder.build();
    this.connections.set(connectionKey, connection);

    try {
      await connection.start();
      console.log(`SignalR Connected successfully to ${serviceName} service:`, hubUrl);
    } catch (err) {
      console.error(`SignalR Connection Error for ${serviceName}:`, err);
      console.error('Connection URL:', hubUrl);
      console.error('Base URL:', baseUrl);
      console.error('Hub Name:', hubName);
      console.error('Connection State:', connection?.state);
      console.error('Full Error Details:', JSON.stringify(err, Object.getOwnPropertyNames(err)));
      
      // Remove failed connection
      this.connections.delete(connectionKey);
      throw err;
    }
  }

  async disconnect(serviceName: string): Promise<void> {
    const connectionKey = this.getConnectionKey(serviceName);
    const connection = this.connections.get(connectionKey);
    
    if (connection) {
      await connection.stop();
      this.connections.delete(connectionKey);
      console.log(`SignalR Disconnected from ${serviceName} service`);
    }
  }

  async disconnectAll(): Promise<void> {
    const promises = Array.from(this.connections.entries()).map(async ([key, connection]) => {
      await connection.stop();
      console.log(`SignalR Disconnected from ${key} service`);
    });
    
    await Promise.all(promises);
    this.connections.clear();
  }

  // Generic upload methods
  // Note: SignalR JavaScript client receives method names in lowercase
  onUploadProgress(serviceName: string, uploadType: string, callback: ProgressCallback): void {
    const connection = this.connections.get(this.getConnectionKey(serviceName));
    const methodName = `${uploadType.toLowerCase()}uploadprogress`;
    connection?.on(methodName, callback);
  }

  onUploadCompleted(serviceName: string, uploadType: string, callback: CompletionCallback): void {
    const connection = this.connections.get(this.getConnectionKey(serviceName));
    const methodName = `${uploadType.toLowerCase()}uploadcompleted`;
    connection?.on(methodName, callback);
  }

  onUploadError(serviceName: string, uploadType: string, callback: ErrorCallback): void {
    const connection = this.connections.get(this.getConnectionKey(serviceName));
    const methodName = `${uploadType.toLowerCase()}uploaderror`;
    connection?.on(methodName, callback);
  }

  // Legacy methods for DRG (for backward compatibility during migration)
  onDrgUploadProgress(serviceName: string, callback: ProgressCallback): void {
    this.onUploadProgress(serviceName, 'Drg', callback);
  }

  onDrgUploadCompleted(serviceName: string, callback: CompletionCallback): void {
    this.onUploadCompleted(serviceName, 'Drg', callback);
  }

  onDrgUploadError(serviceName: string, callback: ErrorCallback): void {
    this.onUploadError(serviceName, 'Drg', callback);
  }

  off(serviceName: string, methodName: string, callback?: (...args: any[]) => void): void {
    const connection = this.connections.get(this.getConnectionKey(serviceName));
    
    if (callback) {
      connection?.off(methodName, callback);
    } else {
      connection?.off(methodName);
    }
  }

  getConnectionState(serviceName: string): signalR.HubConnectionState | null {
    const connection = this.connections.get(this.getConnectionKey(serviceName));
    return connection?.state ?? null;
  }

  isConnected(serviceName: string): boolean {
    const connection = this.connections.get(this.getConnectionKey(serviceName));
    return connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export a singleton instance
export const signalRService = new SignalRService();
